import { useLocalStorage } from '@sixfold/app-utils';
import { ThemeProvider, ThemeObject, useTheme } from '@sixfold/common-ui';
import {
  transporeonLightClassName,
  transporeonDarkClassName,
  trimbleLightClassName,
  trimbleDarkClassName,
} from '@sixfold/common-ui/dist/lib/css';
import transporeonDark from '@sixfold/design-tokens/build/transporeon/json/variables-dark.json';
import transporeonLight from '@sixfold/design-tokens/build/transporeon/json/variables.json';
import trimbleDark from '@sixfold/design-tokens/build/trimble/json/variables-dark.json';
import trimbleLight from '@sixfold/design-tokens/build/trimble/json/variables.json';
import iconsUrl from '@sixfold/icons/dist/sprite.svg';
import * as React from 'react';

import { SHORT_PRODUCT_NAME, TRANSPOREON_PRODUCT_NAME, TRIMBLE_PRODUCT_NAME } from '../constants';
import { getEmbedConfig } from './lib/data';

import '@sixfold/common-ui/dist/lib/styles/global.css';
import '@sixfold/design-tokens/build/transporeon/css/fonts.css';
import '@sixfold/design-tokens/build/transporeon/css/variables.css';
import '@sixfold/design-tokens/build/transporeon/css/variables-dark.css';
import '@sixfold/design-tokens/build/trimble/css/fonts.css';
import '@sixfold/design-tokens/build/trimble/css/variables.css';
import '@sixfold/design-tokens/build/trimble/css/variables-dark.css';

const embedConfig = getEmbedConfig();

const themes: Record<string, ThemeObject> = {
  'transporeon-light': {
    className: transporeonLightClassName,
    variables: transporeonLight,
  },
  'transporeon-dark': {
    className: transporeonDarkClassName,
    variables: transporeonDark,
  },
  'trimble-light': {
    className: trimbleLightClassName,
    variables: trimbleLight,
  },
  'trimble-dark': {
    className: trimbleDarkClassName,
    variables: trimbleDark,
  },
};

const getDefaultTheme = (): string => {
  const configuredTheme = embedConfig?.default_common_ui_theme ?? 'transporeon-light';
  return configuredTheme in themes ? configuredTheme : 'transporeon-light';
};

// Define the function externally, so its identity would remain the same across rerenders.
const getIconPath = (name: string) => `${iconsUrl}#${name}`;

export const ThemeWrapper: React.FunctionComponent<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [theme, setTheme] = useStoredTheme();
  const defaultTheme = getDefaultTheme();

  const currentTheme = embedConfig?.show_common_ui_theme_picker ? theme : defaultTheme;

  return (
    <ThemeProvider currentTheme={currentTheme} themes={themes} onThemeChange={setTheme} getIconPath={getIconPath}>
      {children}
    </ThemeProvider>
  );
};

const useStoredTheme = () => {
  return useLocalStorage<string>('common-ui-theme-v2', getDefaultTheme());
};

export const useProductName = (): string => {
  const { currentTheme } = useTheme();
  return currentTheme?.startsWith('trimble') ? TRIMBLE_PRODUCT_NAME : TRANSPOREON_PRODUCT_NAME;
};

// Intended to be used in header components that use the Trimble logo with "Trimble" text already included, to avoid duplicate "Trimble" part
export const useShortProductName = () => {
  const productName = useProductName();
  return productName === TRIMBLE_PRODUCT_NAME ? SHORT_PRODUCT_NAME : TRANSPOREON_PRODUCT_NAME;
};
